import {
  APP_RELEASE_MAX_STORED,
  type AppReleaseSnapshotPayload,
  PERMISSION_SNAPSHOT_KEY_PREFIX,
  type StoredAppReleases,
} from '@streamlain/permission-snapshot-contract';
import type { LocalAuthorityEnvelope } from '@streamlain/view-config';

import {
  deleteAppReleaseVault,
  readAppReleaseVault,
  stableLocalAuthorityScopeHash,
  writeAppReleaseVault,
} from './local-authority-vault';

export type AppReleaseEnvelope =
  LocalAuthorityEnvelope<AppReleaseSnapshotPayload>;

export interface AppReleaseOwnerScope {
  serverBaseUrl: string;
  userId: string;
}

export interface AppReleaseScope extends AppReleaseOwnerScope {
  workspaceId: string;
  appBaseId: string;
}

/** Why a release request did not return a confirmation. */
export class AppReleaseRequestError extends Error {
  constructor(readonly status: number | null) {
    super(
      status === null
        ? 'App release confirmation unreachable'
        : `App release confirmation refused with ${status}`
    );
    this.name = 'AppReleaseRequestError';
  }
}

export type AppReleaseRequest = (
  scope: AppReleaseScope
) => Promise<AppReleaseEnvelope>;

/**
 * The signed App release confirmation of one person: fetched once per App
 * start while online and kept in the permission vault, read from there while
 * offline. A refused fetch (401/403) drops the App's confirmation; signing
 * out drops all of the person's confirmations on that server.
 */
export class AppReleaseStore {
  constructor(private readonly request: AppReleaseRequest) {}

  async fetchOnStart(scope: AppReleaseScope): Promise<AppReleaseEnvelope> {
    try {
      const envelope = await this.request(scope);
      await this.update(scope, releases => ({
        ...releases,
        [releaseKey(scope)]: { cachedAt: Date.now(), envelope },
      }));
      return envelope;
    } catch (error) {
      if (isRefusal(error)) {
        await this.clear(scope);
        throw error;
      }
      const stored = (await this.read(scope))?.releases[releaseKey(scope)];
      if (!stored) throw error;
      return stored.envelope as AppReleaseEnvelope;
    }
  }

  async clear(
    scope: AppReleaseOwnerScope & { workspaceId?: string; appBaseId?: string }
  ): Promise<void> {
    if (!scope.workspaceId || !scope.appBaseId) {
      await deleteAppReleaseVault(ownerKey(scope));
      return;
    }
    const key = releaseKey({
      ...scope,
      workspaceId: scope.workspaceId,
      appBaseId: scope.appBaseId,
    });
    await this.update(scope, releases =>
      Object.fromEntries(
        Object.entries(releases).filter(([entry]) => entry !== key)
      )
    );
  }

  private async read(
    scope: AppReleaseOwnerScope
  ): Promise<StoredAppReleases | null> {
    const stored = await readAppReleaseVault<StoredAppReleases>(
      ownerKey(scope)
    );
    return stored.status === 'ready' ? stored.value : null;
  }

  private async update(
    scope: AppReleaseOwnerScope,
    change: (
      releases: StoredAppReleases['releases']
    ) => StoredAppReleases['releases']
  ): Promise<void> {
    // The oldest confirmations give way once the vault record is full.
    const releases = Object.fromEntries(
      Object.entries(change((await this.read(scope))?.releases ?? {}))
        .sort(([, left], [, right]) => right.cachedAt - left.cachedAt)
        .slice(0, APP_RELEASE_MAX_STORED)
    );
    if (Object.keys(releases).length === 0) {
      await deleteAppReleaseVault(ownerKey(scope));
      return;
    }
    await writeAppReleaseVault<StoredAppReleases>(ownerKey(scope), {
      version: 1,
      releases,
    });
  }
}

function ownerKey(scope: AppReleaseOwnerScope): string {
  return `${PERMISSION_SNAPSHOT_KEY_PREFIX.appRelease}${stableLocalAuthorityScopeHash(scope.serverBaseUrl)}:${scope.userId}`;
}

function releaseKey(scope: AppReleaseScope): string {
  return `${scope.workspaceId}/${scope.appBaseId}`;
}

function isRefusal(error: unknown): boolean {
  return (
    error instanceof AppReleaseRequestError &&
    (error.status === 401 || error.status === 403)
  );
}
